import React, { useRef, useEffect, useState } from 'react'

interface CollapsiblePanelProps {
  isExpanded: boolean
  onToggle: () => void
  title: string
  children: React.ReactNode
  defaultHeight?: number
  minHeight?: number
  maxHeight?: number
}

export function CollapsiblePanel({
  isExpanded,
  onToggle,
  title,
  children,
  defaultHeight = 300,
  minHeight = 200,
  maxHeight = 600
}: CollapsiblePanelProps) {
  const [height, setHeight] = useState(defaultHeight)
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartY = useRef(0)
  const resizeStartHeight = useRef(0)

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    resizeStartY.current = e.clientY
    resizeStartHeight.current = height
  }

  useEffect(() => {
    if (!isResizing) {
      return
    }

    const handleMouseMove = (e: MouseEvent) => {
      const delta = resizeStartY.current - e.clientY
      const newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStartHeight.current + delta))
      setHeight(newHeight)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, minHeight, maxHeight])

  return (
    <div className={isExpanded ? 'collapsible-panel collapsible-panel--expanded' : 'collapsible-panel'}>
      {isExpanded && (
        <div className="collapsible-panel__resize-handle" onMouseDown={handleResizeStart}>
          <div className="collapsible-panel__resize-indicator" />
        </div>
      )}
      <button type="button" className="collapsible-panel__toggle" onClick={onToggle}>
        <span className="collapsible-panel__toggle-icon">{isExpanded ? '▾' : '▸'}</span>
        <span className="collapsible-panel__title">{title}</span>
      </button>
      {isExpanded && (
        <div className="collapsible-panel__content" style={{ height: `${height}px` }}>
          {children}
        </div>
      )}
    </div>
  )
}
